import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { applyWaitingUpdate } from '../serviceWorkerRegistration';

const isNativeShell = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch (error) {
    return false;
  }
};

const isInstalledPwa = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches
    || window.matchMedia('(display-mode: fullscreen)').matches
    || window.navigator.standalone === true
  );
};

const waitingFrom = (registration) => (
  registration && registration.waiting && navigator.serviceWorker.controller
    ? registration
    : null
);

function UpdateBanner() {
  const [registration, setRegistration] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (isNativeShell() || !('serviceWorker' in navigator)) return undefined;

    const take = (next) => {
      const ready = waitingFrom(next) || waitingFrom(window.__bbSwRegistration);
      if (ready) setRegistration(ready);
    };

    const onUpdate = (event) => {
      take(event.detail);
    };

    take(window.__bbSwRegistration);
    navigator.serviceWorker.getRegistration().then(take).catch(() => {});
    window.addEventListener('bb-sw-update', onUpdate);

    return () => {
      window.removeEventListener('bb-sw-update', onUpdate);
    };
  }, []);

  const apply = useCallback(() => {
    if (applying) return;
    const waiting = waitingFrom(registration) || waitingFrom(window.__bbSwRegistration);
    if (!waiting) {
      setRegistration(null);
      return;
    }
    setApplying(true);
    applyWaitingUpdate(waiting);
  }, [applying, registration]);

  if (isNativeShell() || !registration || !isInstalledPwa()) {
    return null;
  }

  return createPortal(
    <div className="update-banner" role="status" aria-live="polite">
      <div className="update-banner-strip">
        <p className="update-banner-stamp">Fresh</p>
        <div className="update-banner-copy">
          <p className="update-banner-title">New release</p>
          <p className="update-banner-note">
            {applying ? 'Sound check 1.2.3…' : "Tonight's lineup is now live."}
          </p>
        </div>
        <button
          type="button"
          className="update-banner-flip"
          onClick={apply}
          disabled={applying}
        >
          {applying ? 'Going' : "Let's go"}
        </button>
      </div>
    </div>,
    document.body
  );
}

export default UpdateBanner;
